import type { Vec2 } from '../core/math';
import { wrapIndex } from '../core/math';
import { PointGrid } from './pointgrid';

/**
 * Where the barrier actually stands, sample by sample and side by side.
 *
 * The armco used to be a plain offset ring: `width/2 + kerbWidth + wallMargin`
 * everywhere, both sides, unconditionally. That is right on a circuit shaped
 * like a loop and wrong on every circuit that comes back alongside itself,
 * because an offset far enough out to clear one ribbon lands on the next one
 * over. Measured across the hand-traced set, that put barrier on another part
 * of the *racing surface* at 129 samples of Paul Ricard (whose 18 m run-off is
 * the widest here and needs 46 m of self-separation it does not have), 103 of
 * Suzuka, 79 of Kyalami and ten more circuits besides — a fence drawn straight
 * across a road the kart then drives through, since the physics was clamping
 * each kart against its own ribbon's copy of the same ring.
 *
 * So the ring is solved instead of assumed: start at the full margin and walk
 * in until the barrier is clear of every other part of the lap and has not
 * folded through its own centre of curvature, then taper the result so the
 * wall funnels in and out like a real one instead of stepping. Physics and
 * both renderers read the answer, which is the only way the armco you see can
 * be the armco you hit.
 *
 * Deterministic and trig-free, in keeping with `CLAUDE.md` — it reads only the
 * centerline a circuit has already been given.
 */

/** Closest the barrier may ever come to the centerline, as run-off past the
 *  kerb. Below this there is no room to spin a kart into and the wall starts
 *  reading as part of the track edge. */
export const MIN_RUNOFF = 1.5;

/**
 * Depth of the armco rail, front to back.
 *
 * Lives here rather than in the renderer that draws the rail, because it is
 * not only the rail that needs it: the sponsor panels printed on the barrier
 * have to sit proud of its front face or they end up inside it. Two modules in
 * `src/render/three` needing the same number is exactly how those two came to
 * import each other, which left the whole 3D renderer failing to load with a
 * "cannot access before initialization" the moment it was switched on.
 */
export const WALL_DEPTH = 0.16;

/** Clear space demanded between a barrier and any *other* part of the lap,
 *  measured past that part's own kerb. */
const OTHER_CLEARANCE = 0.6;

/** Metres taken off the candidate distance per attempt while walking in. */
const STEP = 0.25;

/** Most the barrier distance may change between neighbouring samples, so the
 *  wall funnels rather than steps. At 2 m spacing this is about a 1:6 taper. */
const MAX_TAPER = 0.35;

/** Lap distance, in metres, within which another sample is simply "the road I
 *  am already on" rather than a different part of the circuit. */
const SELF_SPAN = 45;

export interface BarrierRing {
  /** Distance from the centerline to the barrier at each sample. */
  dist: Float64Array;
  /**
   * 0 where even {@link MIN_RUNOFF} leaves the barrier standing on another
   * part of the circuit — Suzuka's flattened flyover, where the two ribbons
   * genuinely occupy the same ground and no offset can separate them. Nothing
   * is drawn there, because a fence across the racing line is worse than no
   * fence; the physics still clamps at `dist`.
   */
  drawn: Uint8Array;
}

export interface Barriers {
  /** Left of the racing direction — the `+1` side, matching `Track.normal`. */
  left: BarrierRing;
  right: BarrierRing;
}

/**
 * Solve both barrier rings for a centerline.
 *
 * `wallMargin` is the run-off the circuit asks for; the result never exceeds
 * it and only ever comes in.
 */
export function buildBarriers(
  center: readonly Vec2[],
  normal: readonly Vec2[],
  width: number,
  kerbWidth: number,
  wallMargin: number,
  spacing: number,
): Barriers {
  const n = center.length;
  const edge = width / 2 + kerbWidth;
  const full = edge + wallMargin;
  const floor = edge + MIN_RUNOFF;
  const otherReach = edge + OTHER_CLEARANCE;
  const skip = Math.ceil(SELF_SPAN / spacing);

  const grid = new PointGrid(center, Math.max(4, otherReach));

  const rings: BarrierRing[] = [];
  for (const side of [1, -1] as const) {
    const dist = new Float64Array(n);
    const drawn = new Uint8Array(n);

    for (let i = 0; i < n; i++) {
      const j = wrapIndex(i + 1, n);
      const ex = center[j].x - center[i].x;
      const ey = center[j].y - center[i].y;
      const step2 = ex * ex + ey * ey;

      // An offset ring that has folded through its own centre of curvature
      // runs *backwards* against the centerline it came from, so demanding
      // forward progress catches it exactly — the same test the sponsor
      // placer uses for painted run-off.
      const advances = (d: number): boolean => {
        const ax = center[i].x + normal[i].x * d * side;
        const ay = center[i].y + normal[i].y * d * side;
        const bx = center[j].x + normal[j].x * d * side;
        const by = center[j].y + normal[j].y * d * side;
        return (bx - ax) * ex + (by - ay) * ey > 0.15 * step2;
      };

      let d = full;
      while (d > floor) {
        const x = center[i].x + normal[i].x * d * side;
        const y = center[i].y + normal[i].y * d * side;
        if (advances(d) && grid.clearOfOthers(x, y, otherReach, i, skip, n)) break;
        d -= STEP;
      }
      dist[i] = Math.max(d, floor);
    }

    // Funnel rather than step. Two wrapping passes are enough to settle a
    // ring: each one can only lower a sample towards its neighbour, so the
    // result is order-independent and the second pass closes the seam the
    // first one opened at index 0.
    for (let pass = 0; pass < 2; pass++) {
      for (let i = n - 1; i >= 0; i--) {
        const nxt = dist[wrapIndex(i + 1, n)];
        if (dist[i] > nxt + MAX_TAPER) dist[i] = nxt + MAX_TAPER;
      }
      for (let i = 0; i < n; i++) {
        const prv = dist[wrapIndex(i - 1, n)];
        if (dist[i] > prv + MAX_TAPER) dist[i] = prv + MAX_TAPER;
      }
    }

    // Whether a stretch can be shown is decided on the *final* distances, not
    // the ones the walk-in proposed: the taper above only ever pulls a sample
    // inward, and at a crossing — where the other ribbon runs straight through
    // this one — inward is not automatically clearer.
    for (let i = 0; i < n; i++) {
      const d = dist[i];
      const x = center[i].x + normal[i].x * d * side;
      const y = center[i].y + normal[i].y * d * side;
      drawn[i] = grid.clearOfOthers(x, y, otherReach, i, skip, n) ? 1 : 0;
    }

    rings.push({ dist, drawn });
  }

  return { left: rings[0], right: rings[1] };
}
